/**
 * DaVinci API Response Schemas
 *
 * Response schemas for DaVinci REST API endpoints.
 * Includes booking responses, errors, and action results.
 */

import { z } from 'zod';

// ----- Booking Response -----

/**
 * Booking response object (simplified - uses flexible schema for complex structure)
 */
export const DaVinciBookingResponseSchema = z.object({
  ActionCode: z.string().nullable(),
  AgencyNumber: z.number(),
  BookingState: z.string().optional(),
  LanguageCode: z.string().optional(),
  Touroperator: z.string().optional(),
  TravelType: z.string().optional(),
  ReturnCode: z.number().optional(),
  ReturnCodeDescription: z.string().optional(),
  Services: z.array(z.record(z.unknown())).optional(),
  Client: z.record(z.unknown()).optional(),
  Participants: z.array(z.record(z.unknown())).optional(),
});

// ----- API Responses -----

/**
 * PerformAction response
 * Note: ReturnCode is inside Booking object, not at top-level
 */
export const PerformActionResponseSchema = z.object({
  SessionId: z.string(),
  ActivityId: z.string(),
  Booking: z.record(z.unknown()), // Flexible booking object with ReturnCode inside
  Success: z.boolean(),
  Delivery: z.record(z.unknown()).optional(),
  Payment: z.record(z.unknown()).optional(),
});

/**
 * ConvertResponseToNewRequest response
 */
export const ConvertResponseResponseSchema = z.object({
  SessionId: z.string(),
  ActivityId: z.string(),
  Booking: z.record(z.unknown()), // Transformed booking object
});

// ----- Error Response -----

/**
 * Error response
 */
export const DaVinciErrorResponseSchema = z.object({
  error: z
    .object({
      code: z.string(),
      message: z.string(),
    })
    .optional(),
  ReturnCode: z.number().optional(),
  ReturnMessage: z.string().optional(),
});

// ----- Type Exports -----

export type DaVinciBookingResponse = z.infer<typeof DaVinciBookingResponseSchema>;
export type PerformActionResponse = z.infer<typeof PerformActionResponseSchema>;
export type ConvertResponseResponse = z.infer<typeof ConvertResponseResponseSchema>;
export type DaVinciErrorResponse = z.infer<typeof DaVinciErrorResponseSchema>;
